<template>
    <div v-if="currentFile" class="lightbox" @click.self="close">
        <div class="lightbox-toolbar">
            <button class="lightbox-btn" aria-label="Zoom out" :disabled="currentZoom <= 1" @click.stop="zoomOut">
                <i class="bi bi-zoom-out"></i>
            </button>
            <button class="lightbox-btn" aria-label="Zoom in" :disabled="currentZoom >= 5" @click.stop="zoomIn">
                <i class="bi bi-zoom-in"></i>
            </button>
            <button class="lightbox-btn" aria-label="Rotate" @click.stop="rotateImage">
                <i class="bi bi-arrow-clockwise"></i>
            </button>
            <a :href="currentFile.download_url || currentFile.url" :download="currentFile.file_name"
                class="lightbox-btn" aria-label="Download" @click.stop>
                <i class="bi bi-download"></i>
            </a>
            <button class="lightbox-btn" aria-label="Close" @click="close">
                <i class="bi bi-x-lg"></i>
            </button>
        </div>

        <button v-if="images.length > 1" class="lightbox-btn lightbox-nav prev" aria-label="Previous"
            @click.stop="prevImage">
            <i class="bi bi-chevron-left"></i>
        </button>

        <div class="lightbox-stage" @click.self="close">
            <img :src="currentFile.url" class="lightbox-image" :alt="currentFile.file_name"
                :class="{ 'is-zoomed': currentZoom > 1, 'is-panning': isPanning }"
                :style="{ transform: `translate(${panX}px, ${panY}px) scale(${currentZoom}) rotate(${currentRotation}deg)` }"
                :draggable="false" @wheel.prevent="handleWheel" @mousedown.prevent="startPan" @dblclick="resetZoom"
                @touchstart="handleTouchStart" @touchmove.prevent="handleTouchMove" @touchend="handleTouchEnd" />
            <div class="lightbox-caption">
                <span class="name" :title="currentFile.file_name">{{ truncateMiddle(currentFile.file_name, 60)
                }}</span>
                <div class="lightbox-meta">
                    <span v-if="currentFile.created_at">{{ format(new Date(currentFile.created_at), "d MMM yyyy")
                        }}</span>
                    <span v-if="images.length > 1" class="counter">
                        {{ currentIndex + 1 }} / {{ images.length }}
                    </span>
                </div>
            </div>
        </div>

        <button v-if="images.length > 1" class="lightbox-btn lightbox-nav next" aria-label="Next"
            @click.stop="nextImage">
            <i class="bi bi-chevron-right"></i>
        </button>
    </div>
</template>

<script>
import { format } from "date-fns";

export default {
    name: "Lightbox",
    props: {
        images: {
            type: Array,
            required: true,
        },
        initialIndex: {
            type: Number,
            default: 0,
        },
    },
    emits: ["close"],
    data() {
        return {
            currentIndex: this.initialIndex,
            currentRotation: 0,
            currentZoom: 1,
            panX: 0,
            panY: 0,
            isPanning: false,
            panStart: null,
            pinchStart: null,
        };
    },
    computed: {
        currentFile() {
            return this.images[this.currentIndex] || null;
        },
    },
    mounted() {
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", this.handleKeydown);
    },
    unmounted() {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", this.handleKeydown);
        document.removeEventListener("mousemove", this.handlePan);
        document.removeEventListener("mouseup", this.endPan);
    },
    methods: {
        close() {
            this.endPan();
            this.$emit("close");
        },
        nextImage() {
            if (!this.images.length) return;
            this.currentIndex = (this.currentIndex + 1) % this.images.length;
            this.resetView();
        },
        prevImage() {
            if (!this.images.length) return;
            this.currentIndex =
                (this.currentIndex - 1 + this.images.length) % this.images.length;
            this.resetView();
        },
        resetView() {
            this.currentRotation = 0;
            this.resetZoom();
        },
        rotateImage() {
            this.currentRotation = (this.currentRotation + 90) % 360;
        },
        zoomIn() {
            this.setZoom(this.currentZoom * 1.5);
        },
        zoomOut() {
            this.setZoom(this.currentZoom / 1.5);
        },
        resetZoom() {
            this.currentZoom = 1;
            this.panX = 0;
            this.panY = 0;
        },
        setZoom(newZoom) {
            const clamped = Math.min(Math.max(newZoom, 1), 5);
            if (clamped === 1) {
                this.resetZoom();
                return;
            }
            this.currentZoom = clamped;
        },
        handleWheel(e) {
            const rect = e.currentTarget.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const mouseX = e.clientX - centerX;
            const mouseY = e.clientY - centerY;

            const delta = e.deltaY < 0 ? 1.1 : 1 / 1.1;
            const newZoom = Math.min(Math.max(this.currentZoom * delta, 1), 5);

            if (newZoom === 1) {
                this.resetZoom();
                return;
            }

            const factor = newZoom / this.currentZoom;
            this.panX = mouseX - (mouseX - this.panX) * factor;
            this.panY = mouseY - (mouseY - this.panY) * factor;
            this.currentZoom = newZoom;
        },
        startPan(e) {
            if (this.currentZoom <= 1) return;
            this.isPanning = true;
            this.panStart = {
                x: e.clientX,
                y: e.clientY,
                panX: this.panX,
                panY: this.panY,
            };
            document.addEventListener("mousemove", this.handlePan);
            document.addEventListener("mouseup", this.endPan);
        },
        handlePan(e) {
            if (!this.isPanning || !this.panStart) return;
            this.panX = this.panStart.panX + (e.clientX - this.panStart.x);
            this.panY = this.panStart.panY + (e.clientY - this.panStart.y);
        },
        endPan() {
            this.isPanning = false;
            this.panStart = null;
            document.removeEventListener("mousemove", this.handlePan);
            document.removeEventListener("mouseup", this.endPan);
        },
        handleTouchStart(e) {
            if (e.touches.length === 2) {
                this.pinchStart = {
                    distance: this.touchDistance(e.touches),
                    zoom: this.currentZoom,
                };
            } else if (e.touches.length === 1 && this.currentZoom > 1) {
                this.isPanning = true;
                this.panStart = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                    panX: this.panX,
                    panY: this.panY,
                };
            }
        },
        handleTouchMove(e) {
            if (e.touches.length === 2 && this.pinchStart) {
                const distance = this.touchDistance(e.touches);
                const newZoom = Math.min(
                    Math.max(this.pinchStart.zoom * (distance / this.pinchStart.distance), 1),
                    5
                );
                if (newZoom === 1) this.resetZoom();
                else this.currentZoom = newZoom;
            } else if (e.touches.length === 1 && this.isPanning && this.panStart) {
                this.panX = this.panStart.panX + (e.touches[0].clientX - this.panStart.x);
                this.panY = this.panStart.panY + (e.touches[0].clientY - this.panStart.y);
            }
        },
        handleTouchEnd() {
            this.endPan();
            this.pinchStart = null;
        },
        touchDistance(touches) {
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        },
        handleKeydown(e) {
            if (e.key === "Escape") this.close();
            if (e.key === "ArrowRight") this.nextImage();
            if (e.key === "ArrowLeft") this.prevImage();
        },
        format,
        truncateMiddle(str, max) {
            if (!str || str.length <= max) return str;
            const keep = max - 3;
            const front = Math.ceil(keep * 0.5);
            const back = keep - front;
            return str.slice(0, front) + "..." + str.slice(-back);
        },
    },
};
</script>

<style scoped lang="scss">
.lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 60px 80px;
}

.lightbox-stage {
    position: relative;
    max-width: 100%;
    max-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.lightbox-image {
    max-width: 100%;
    max-height: calc(100vh - 140px);
    object-fit: contain;
    border-radius: 4px;
    transition: transform 0.2s ease;
    user-select: none;
    -webkit-user-drag: none;
    will-change: transform;
    touch-action: none;
}

.lightbox-image.is-zoomed {
    cursor: grab;
}

.lightbox-image.is-panning {
    cursor: grabbing;
    transition: none;
}

.lightbox-caption {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
}

.lightbox-caption .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.lightbox-meta {
    flex-shrink: 0;
    display: flex;
    gap: 14px;
    color: rgba(255, 255, 255, 0.6);
}

.lightbox-caption .counter {
    color: rgba(255, 255, 255, 0.6);
}

.lightbox-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s ease;
}

.lightbox-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
}

.lightbox-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.lightbox-toolbar {
    position: absolute;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 8px;
    z-index: 1;
}

.lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
}

.lightbox-nav.prev {
    left: 300px;
}

.lightbox-nav.next {
    right: 300px;
}

@media (max-width: 576px) {
    .lightbox {
        padding: 50px 12px;
    }

    .lightbox-toolbar {
        top: 12px;
        right: 12px;
    }

    .lightbox-nav.prev {
        left: 40px;
    }

    .lightbox-nav.next {
        right: 40px;
    }
}
</style>
